import type { LeaderboardClient } from "@yjsgames/leaderboard";
import { GameCenterNative, type GameCenterNativePlugin } from "./gameCenterPlugin.js";
import { diag } from "../app/diag.js";

const log = diag("gameCenter");

/**
 * iOS 리더보드 클라이언트 — GameKit Game Center(GKLeaderboard)를 네이티브 플러그인으로 래핑.
 * 생성 시 인증(GKLocalPlayer)을 시작하고 그 Promise를 보관한다. 모든 호출은 graceful(throw 흡수).
 * native는 생성자 주입이라 단위테스트에서 mock 가능.
 *
 * ⚠ race 방지: 인증은 비동기라 부팅 직후엔 미완일 수 있다. `isAvailable()`은 (iOS에서) true를 반환하고,
 * 실제 인증 완료 판정은 `submitScore`가 init Promise를 await한 뒤 수행한다 — 부팅 직후 빠른 chapter_clear가
 * 인증 완료 전 stale false로 제출을 건너뛰던 race를 제거(인증 끝나면 그 제출이 정상 반영).
 */
export class GameCenterLeaderboardClient implements LeaderboardClient {
  /** 인증 완료 여부 Promise — submitScore가 await해 race 없이 판정. */
  private readonly ready: Promise<boolean>;

  constructor(private readonly native: GameCenterNativePlugin = GameCenterNative) {
    this.ready = this.init();
  }

  private async init(): Promise<boolean> {
    try {
      const authed = (await this.native.isAvailable()).available;
      log.diag("init", `authenticated=${authed}`);
      return authed;
    } catch (e) {
      log.diag("init", `failed: ${(e as Error).message}`);
      return false;
    }
  }

  /** iOS에서 Game Center는 가용 — 실제 인증 완료는 submitScore가 ready를 await해 확인. */
  isAvailable(): boolean {
    return true;
  }

  async submitScore(leaderboardId: string, score: number): Promise<void> {
    if (!(await this.ready)) return; // 인증 완료 대기 후 판정 — 부팅 직후 race 제거
    try {
      await this.native.submitScore({ leaderboardId, score });
    } catch (e) {
      log.diag("submitScore", `${leaderboardId} ${score} failed: ${(e as Error).message}`);
    }
  }
}
